import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import classes from "./SDGGoals.module.css";
import { getSDGIcon } from "./SDGIcons";
import { BaseURL } from "../home/BaseData";

const SDG_COLORS = {
  1: "#FF7A00",
  2: "#EAB308",
  3: "#22C55E",
  4: "#EF4444",
  5: "#EC4899",
  6: "#3B82F6",
  7: "#F97316",
  8: "#06B6D4",
  9: "#64748B",
  10: "#F59E0B",
  11: "#8B5CF6",
  12: "#84CC16",
  13: "#64748B",
  14: "#0284C7",
  15: "#10B981",
  16: "#F43F5E",
  17: "#14B8A6",
};

const SLIDE_INTERVAL_MS = 7000;

/* Saytda tayyor turadigan matnlar — admin kiritmaydi. */
const FALLBACK = {
  title: {
    uz: "Barqaror Rivojlanish Maqsadlari (SDG)",
    en: "Sustainable Development Goals (SDGs)",
    ru: "Цели устойчивого развития (ЦУР)",
  },
  goalsHeader: {
    uz: "Barqaror rivojlanish maqsadlari",
    en: "Sustainable Development Goals",
    ru: "Цели устойчивого развития",
  },
  goalsEyebrow: {
    uz: "GLOBAL MAQSADLAR",
    en: "GLOBAL GOALS",
    ru: "ГЛОБАЛЬНЫЕ ЦЕЛИ",
  },
  goalsPortal: {
    uz: "Rasmiy BRM portaliga o'ting",
    en: "Go to the official SDG portal",
    ru: "Перейти на официальный портал ЦУР",
  },
  initiativesEyebrow: {
    uz: "TASHABBUSLAR VA AMALIY ISHLAR",
    en: "INITIATIVES AND PRACTICAL WORK",
    ru: "ИНИЦИАТИВЫ И ПРАКТИЧЕСКАЯ РАБОТА",
  },
  initiativesTitle: {
    uz: "Tashabbuslar va amaliy ishlar",
    en: "Initiatives and practical work",
    ru: "Инициативы и практическая работа",
  },
  strategyEyebrow: {
    uz: "STRATEGIYA VA YO'NALISH",
    en: "STRATEGY AND DIRECTION",
    ru: "СТРАТЕГИЯ И НАПРАВЛЕНИЯ",
  },
  strategyTitle: {
    uz: "Strategiya va yo'nalish",
    en: "Strategy and direction",
    ru: "Стратегия и направления",
  },
  viewInitiative: {
    uz: "Tashabbusni ko'rish",
    en: "View initiative",
    ru: "Смотреть инициативу",
  },
  watchVideo: {
    uz: "VIDEONI KO'RISH",
    en: "WATCH THE VIDEO",
    ru: "СМОТРЕТЬ ВИДЕО",
  },
  more: { uz: "Batafsil", en: "Learn more", ru: "Подробнее" },
  commitmentEyebrow: {
    uz: "BIZNING MAJBURIYATIMIZ",
    en: "OUR COMMITMENT",
    ru: "НАШЕ ОБЯЗАТЕЛЬСТВО",
  },
  historyTitle: {
    uz: "YILLAR BO'YICHA O'SISH",
    en: "PROGRESS BY YEAR",
    ru: "ДИНАМИКА ПО ГОДАМ",
  },
  nextGoalEyebrow: {
    uz: "KEYINGI MAQSADIMIZ",
    en: "OUR NEXT GOAL",
    ru: "НАША СЛЕДУЮЩАЯ ЦЕЛЬ",
  },
  teamEyebrow: {
    uz: "BARQARORLIK JAMOASI",
    en: "SUSTAINABILITY TEAM",
    ru: "КОМАНДА УСТОЙЧИВОГО РАЗВИТИЯ",
  },
  contactEyebrow: {
    uz: "UNIVERSITETGA TASHRIF",
    en: "VISIT THE UNIVERSITY",
    ru: "ПОСЕТИТЬ УНИВЕРСИТЕТ",
  },
  prev: { uz: "Oldingi", en: "Previous", ru: "Назад" },
  next: { uz: "Keyingi", en: "Next", ru: "Вперёд" },
  pause: { uz: "To'xtatish", en: "Pause", ru: "Пауза" },
  play: { uz: "Davom ettirish", en: "Play", ru: "Продолжить" },
  map: {
    uz: "Google Xaritalarda ochish",
    en: "Open in Google Maps",
    ru: "Открыть в Google Картах",
  },
  loading: { uz: "Yuklanmoqda...", en: "Loading...", ru: "Загрузка..." },
};

/** Oddiy matnni xatboshilarga ajratadi. */
const paragraphs = (text) =>
  (text || "")
    .split(/\n{1,}/)
    .map((t) => t.trim())
    .filter(Boolean);

const SDGGoals = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const [sdgInfo, setSdgInfo] = useState(null);
  const [goals, setGoals] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [commitment, setCommitment] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [initiativeSection, setInitiativeSection] = useState(null);
  const [strategy, setStrategy] = useState([]);
  const [contact, setContact] = useState(null);
  const [video, setVideo] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [recognition, setRecognition] = useState(null);
  const [feature, setFeature] = useState(null);
  const [teamSection, setTeamSection] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const [slideIndex, setSlideIndex] = useState(0);
  const [slidePaused, setSlidePaused] = useState(false);

  useEffect(() => {
    const fetchSDGData = async () => {
      try {
        const urlsToTry = [
          `${BaseURL.replace(/\/$/, '')}/api/sdg/`,
          "http://127.0.0.1:8001/api/sdg/",
          "http://127.0.0.1:8000/api/sdg/",
          "http://localhost:8000/api/sdg/",
          "http://localhost:8001/api/sdg/"
        ];
        let response = null;
        for (const url of urlsToTry) {
          try {
            response = await axios.get(url);
            if (response && response.data && response.data.goals) break;
          } catch (e) {
            // try next
          }
        }

        if (response && response.data) {
          const data = response.data;
          setSdgInfo(data.info);
          setGoals(data.goals || []);
          // Quyidagi bo'limlar serverda hali bo'lmasligi ham mumkin —
          // shunda ular bo'sh qoladi va saytda umuman chizilmaydi.
          setHeroSlides(data.hero_slides || []);
          setCommitment(data.commitment || null);
          setInitiatives(data.initiatives || []);
          setInitiativeSection(data.initiative_section || null);
          setStrategy(data.strategy || []);
          setContact(data.contact || null);
          setVideo(data.video || null);
          setRanking(data.ranking || null);
          setRecognition(data.recognition || null);
          setFeature(data.feature || null);
          setTeamSection(data.team_section || null);
          setTeam(data.team || []);
        }
      } catch (error) {
        console.error("Error fetching SDG data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSDGData();
  }, []);

  const lang = i18n.language || "uz";

  // Tanlangan tilda bo'sh bo'lsa o'zbekchaga qaytadi — bo'sh sarlavha chiqmasin.
  const L = useCallback(
    (obj, prefix) => {
      if (!obj) return "";
      return obj[`${prefix}_${lang}`] || obj[`${prefix}_uz`] || "";
    },
    [lang]
  );

  const T = useCallback((key) => FALLBACK[key][lang] || FALLBACK[key].uz, [lang]);

  const mediaUrl = useCallback((path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${BaseURL.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`;
  }, []);

  const slides = useMemo(
    () => heroSlides.filter((s) => L(s, "title") || s.image),
    [heroSlides, L]
  );

  useEffect(() => {
    if (slides.length < 2 || slidePaused) return undefined;
    const id = setInterval(
      () => setSlideIndex((i) => (i + 1) % slides.length),
      SLIDE_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [slides.length, slidePaused]);

  useEffect(() => {
    if (slideIndex >= slides.length) setSlideIndex(0);
  }, [slides.length, slideIndex]);

  if (loading) {
    return <div className={classes.loading}>{T("loading")}</div>;
  }

  const goalsHeader = L(sdgInfo, "goals_header") || T("goalsHeader");
  const pageTitle = L(sdgInfo, "title") || T("title");
  const pageSubtitle = L(sdgInfo, "subtitle");

  const commitmentPoints = (commitment?.points || []).filter((p) => L(p, "text"));
  const recCards = recognition?.cards || [];
  const [leadCard, ...restCards] = recCards;
  const showAwards =
    (ranking && (ranking.value || L(ranking, "description"))) ||
    (recognition && (L(recognition, "title") || recCards.length > 0));

  return (
    <div className={classes.page}>
      {/* ---------------- Hero slayder ---------------- */}
      {slides.length > 0 && (
        <section className={classes.hero} aria-label={pageTitle}>
          {slides.map((slide, idx) => {
            const img = mediaUrl(slide.image);
            return (
              <div
                key={slide.id}
                className={`${classes.heroSlide} ${idx === slideIndex ? classes.heroSlideActive : ""}`}
                aria-hidden={idx !== slideIndex}
              >
                {img && <img src={img} alt="" className={classes.heroImg} />}
                <div className={classes.heroScrim} />
                <div className={classes.heroInner}>
                  {L(slide, "eyebrow") && (
                    <span className={classes.heroEyebrow}>{L(slide, "eyebrow")}</span>
                  )}
                  <h1
                    className={`${classes.heroTitle} ${
                      L(slide, "title").length > 70 ? classes.heroTitleLong : ""
                    }`}
                  >
                    {L(slide, "title")}
                  </h1>
                  {L(slide, "description") && (
                    <p className={classes.heroText}>{L(slide, "description")}</p>
                  )}
                  {slide.button_url && (
                    <a className={classes.heroBtn} href={slide.button_url}>
                      {T("more")}
                      <span aria-hidden="true"> →</span>
                    </a>
                  )}
                </div>
              </div>
            );
          })}

          {/* To'q-sariq nom bandi */}
          <div className={classes.heroBand}>
            <span className={classes.heroBandTitle}>{pageTitle}</span>
            {pageSubtitle && (
              <span className={classes.heroBandSub}>{pageSubtitle}</span>
            )}
          </div>

          {slides.length > 1 && (
            <div className={classes.heroControls}>
              <button
                type="button"
                className={classes.heroCtrlBtn}
                aria-label={T("prev")}
                onClick={() =>
                  setSlideIndex((i) => (i - 1 + slides.length) % slides.length)
                }
              >
                ←
              </button>
              <span className={classes.heroDots}>
                {slides.map((s, i) => (
                  <span
                    key={s.id}
                    className={`${classes.heroDot} ${i === slideIndex ? classes.heroDotOn : ""}`}
                    aria-hidden="true"
                  />
                ))}
              </span>
              <button
                type="button"
                className={classes.heroCtrlBtn}
                aria-label={T("next")}
                onClick={() => setSlideIndex((i) => (i + 1) % slides.length)}
              >
                →
              </button>
              <button
                type="button"
                className={classes.heroCtrlBtn}
                aria-label={slidePaused ? T("play") : T("pause")}
                onClick={() => setSlidePaused((p) => !p)}
              >
                {slidePaused ? "▶" : "❙❙"}
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- Sahifa sarlavhasi (slayder bo'lmasa) ---------------- */}
      {slides.length === 0 && (
        <section className={`${classes.section} ${classes.pageHead}`}>
          <h1 className={classes.pageTitle}>{pageTitle}</h1>
        </section>
      )}

      {/* ---------------- Majburiyat ---------------- */}
      {commitment && (L(commitment, "title") || L(commitment, "description")) && (
        <section className={`${classes.section} ${classes.commitment}`}>
          <div>
            <span className={classes.eyebrow}>{T("commitmentEyebrow")}</span>
            {L(commitment, "title") && (
              <h2 className={classes.displayTitle}>{L(commitment, "title")}</h2>
            )}
          </div>

          <div className={classes.commitmentSide}>
            {commitment.image && (
              <img
                src={mediaUrl(commitment.image)}
                alt=""
                className={classes.commitmentImg}
              />
            )}
            {paragraphs(L(commitment, "description")).map((t, i) => (
              <p key={i} className={classes.lead}>
                {t}
              </p>
            ))}

            {commitmentPoints.length > 0 && (
              <ul className={classes.checkList}>
                {commitmentPoints.map((p) => (
                  <li key={p.id} className={classes.checkItem}>
                    <span className={classes.checkMark} aria-hidden="true">
                      ✓
                    </span>
                    {L(p, "text")}
                  </li>
                ))}
              </ul>
            )}

            {commitment.button_url && (
              <a className={classes.textLink} href={commitment.button_url}>
                {T("more")}
                <span aria-hidden="true"> →</span>
              </a>
            )}
          </div>
        </section>
      )}

      {/* ---------------- 17 maqsad ---------------- */}
      {goals.length > 0 && (
        <section className={`${classes.section} ${classes.goalsSection}`}>
          <span className={classes.eyebrow}>{T("goalsEyebrow")}</span>
          <h2 className={classes.displayTitle}>{goalsHeader}</h2>

          <div className={classes.goalsTools}>
            {sdgInfo?.goals_portal_url && (
              <a className={classes.darkBtn} href={sdgInfo.goals_portal_url}>
                {T("goalsPortal")}
                <span aria-hidden="true"> ↗</span>
              </a>
            )}
          </div>

          <div className={classes.goalsWrapper}>
            <div className={classes.goalsGrid}>
              {goals.map((goal) => {
                const goalTitle = L(goal, "title");
                const badgeColor = goal.color || SDG_COLORS[goal.number] || "#E5243B";
                const imageUrl = mediaUrl(goal.icon);

                return (
                  <button
                    type="button"
                    key={goal.id || goal.number}
                    className={classes.goalCard}
                    onClick={() => navigate(`/sdg/${goal.number || goal.slug}`)}
                  >
                    <span className={classes.circleWrapper}>
                      <span
                        className={classes.circleBg}
                        style={{ backgroundColor: `${badgeColor}25` }}
                      >
                        {imageUrl ? (
                          <img src={imageUrl} alt="" className={classes.circleImg} />
                        ) : (
                          getSDGIcon(goal.number, badgeColor)
                        )}
                      </span>
                      <span
                        className={classes.numberBadge}
                        style={{ backgroundColor: badgeColor }}
                      >
                        {goal.number}
                      </span>
                    </span>
                    <span className={classes.cardTitle}>{goalTitle}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- Video bloki ---------------- */}
      {video && (L(video, "title") || video.image) && (
        <section className={classes.videoBlock}>
          {L(video, "eyebrow") && (
            <span className={classes.videoSideLabel} aria-hidden="true">
              {L(video, "eyebrow")}
            </span>
          )}

          <div className={classes.videoFrame}>
            {video.image && (
              <img src={mediaUrl(video.image)} alt="" className={classes.videoImg} />
            )}
            <div className={classes.videoScrim} />

            <div className={classes.videoInner}>
              <div className={classes.videoText}>
                {L(video, "eyebrow") && (
                  <span className={classes.videoEyebrow}>{L(video, "eyebrow")}</span>
                )}
                {L(video, "title") && (
                  <h2
                    className={`${classes.videoTitle} ${
                      L(video, "title").length > 70 ? classes.videoTitleLong : ""
                    }`}
                  >
                    {L(video, "title")}
                  </h2>
                )}
                {L(video, "description") && (
                  <p className={classes.videoDesc}>{L(video, "description")}</p>
                )}
              </div>

              {video.video_url && (
                <a
                  className={classes.videoBtn}
                  href={video.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className={classes.videoPlay} aria-hidden="true">
                    ▶
                  </span>
                  <span className={classes.videoBtnText}>{T("watchVideo")}</span>
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- Reyting va xalqaro e'tirof ---------------- */}
      {showAwards && (
        <section className={classes.awards}>
          <div className={classes.awardsInner}>
            {/* Chap: to'q reyting kartasi */}
            {ranking && (ranking.value || L(ranking, "description")) && (
              <aside className={classes.rankPanel}>
                {L(ranking, "eyebrow") && (
                  <span className={classes.rankPanelEyebrow}>{L(ranking, "eyebrow")}</span>
                )}
                {ranking.value && (
                  <div className={classes.rankPanelValue}>{ranking.value}</div>
                )}
                {L(ranking, "description") && (
                  <p className={classes.rankPanelText}>{L(ranking, "description")}</p>
                )}

                {(ranking.years || []).length > 0 && (
                  <>
                    <span className={classes.rankPanelSub}>{T("historyTitle")}</span>
                    <ul className={classes.yearList}>
                      {ranking.years.map((y) => (
                        <li key={y.id} className={classes.yearItem}>
                          <span className={classes.yearLabel}>{y.year}</span>
                          <span className={classes.yearValue}>
                            {y.value}
                            {L(y, "note") && (
                              <span className={classes.yearNote}> {L(y, "note")}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {ranking.link_url && (
                  <a className={classes.rankPanelLink} href={ranking.link_url}>
                    {T("more")}
                    <span aria-hidden="true"> ↗</span>
                  </a>
                )}
              </aside>
            )}

            {/* O'rta: e'tirof */}
            <div className={classes.awardsMain}>
              {L(recognition, "eyebrow") && (
                <span className={classes.awardsEyebrow}>{L(recognition, "eyebrow")}</span>
              )}
              {L(recognition, "title") && (
                <h2 className={classes.awardsTitle}>{L(recognition, "title")}</h2>
              )}
              {L(recognition, "description") && (
                <p className={classes.awardsLead}>{L(recognition, "description")}</p>
              )}

              {leadCard && (
                <div className={classes.leadCard}>
                  <span className={classes.leadCardLabel}>{L(leadCard, "label")}</span>
                  <span className={classes.leadCardValue}>{leadCard.value}</span>
                  {L(leadCard, "note") && (
                    <span className={classes.leadCardNote}>{L(leadCard, "note")}</span>
                  )}
                </div>
              )}

              {restCards.length > 0 && (
                <div className={classes.rankCards}>
                  {restCards.map((c) => (
                    <div key={c.id} className={classes.rankCard}>
                      <span className={classes.rankCardLabel}>{L(c, "label")}</span>
                      <span className={classes.rankCardValue}>{c.value}</span>
                      {L(c, "note") && (
                        <span className={classes.rankCardNote}>{L(c, "note")}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* O'ng: keyingi maqsad */}
            {recognition &&
              (L(recognition, "goal_title") ||
                (recognition.goal_points || []).length > 0) && (
                <aside className={classes.nextGoal}>
                  <span className={classes.nextGoalEyebrow}>{T("nextGoalEyebrow")}</span>
                  {L(recognition, "goal_title") && (
                    <h3 className={classes.nextGoalTitle}>
                      {L(recognition, "goal_title")}
                    </h3>
                  )}
                  {L(recognition, "goal_description") && (
                    <p className={classes.nextGoalText}>
                      {L(recognition, "goal_description")}
                    </p>
                  )}

                  {(recognition.goal_points || []).length > 0 && (
                    <ul className={classes.dotList}>
                      {recognition.goal_points.map((p) => (
                        <li key={p.id} className={classes.dotItem}>
                          <span className={classes.dotMark} aria-hidden="true" />
                          {L(p, "text")}
                        </li>
                      ))}
                    </ul>
                  )}
                </aside>
              )}
          </div>
        </section>
      )}

      {/* ---------------- Tashabbuslar ---------------- */}
      {initiatives.length > 0 && (
        <section className={`${classes.section} ${classes.initiatives}`}>
          <div className={classes.splitHead}>
            <div>
              <span className={classes.eyebrow}>{T("initiativesEyebrow")}</span>
              <h2 className={classes.displayTitle}>
                {L(initiativeSection, "title") || T("initiativesTitle")}
              </h2>
            </div>
            {L(initiativeSection, "description") && (
              <p className={classes.lead}>{L(initiativeSection, "description")}</p>
            )}
          </div>

          <div className={classes.mosaic}>
            {initiatives.map((item, idx) => {
              const img = mediaUrl(item.image);
              // Tashqi havola kiritilgan bo'lsa karta o'sha havolani ochadi,
              // aks holda shu saytdagi batafsil sahifani.
              const external = Boolean(item.link_url);
              const Card = external ? "a" : "button";
              const cardProps = external
                ? { href: item.link_url, target: "_blank", rel: "noopener noreferrer" }
                : {
                    type: "button",
                    onClick: () =>
                      navigate(`/sdg/initiative/${item.slug || item.id}`),
                  };
              return (
                <Card
                  key={item.id}
                  className={`${classes.tile} ${idx === 0 ? classes.tileBig : ""}`}
                  {...cardProps}
                >
                  {img ? (
                    <img src={img} alt="" className={classes.tileImg} />
                  ) : (
                    <span className={classes.tilePlaceholder} aria-hidden="true" />
                  )}
                  <span className={classes.tileScrim} aria-hidden="true" />
                  <span className={classes.tileBody}>
                    {L(item, "category") && (
                      <span className={classes.tileEyebrow}>{L(item, "category")}</span>
                    )}
                    <span className={classes.tileTitle}>{L(item, "title")}</span>
                    <span className={classes.tileLink}>
                      {T("viewInitiative")}
                      <span aria-hidden="true"> ↗</span>
                    </span>
                  </span>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------------- Tanlangan bo'lim ---------------- */}
      {feature && (L(feature, "title") || feature.image) && (
        <section className={classes.feature}>
          <div className={classes.featureMedia}>
            {feature.image ? (
              <img src={mediaUrl(feature.image)} alt="" className={classes.featureImg} />
            ) : (
              <div className={classes.featurePlaceholder} aria-hidden="true" />
            )}
          </div>

          <div className={classes.featureText}>
            {L(feature, "eyebrow") && (
              <span className={classes.eyebrow}>{L(feature, "eyebrow")}</span>
            )}
            {L(feature, "title") && (
              <h2 className={classes.displayTitle}>{L(feature, "title")}</h2>
            )}
            {paragraphs(L(feature, "description")).map((t, i) => (
              <p key={i} className={classes.lead}>
                {t}
              </p>
            ))}

            {(feature.points || []).length > 0 && (
              <ul className={classes.featureList}>
                {feature.points.map((p) =>
                  p.link_url ? (
                    <li key={p.id}>
                      <a className={classes.featureLink} href={p.link_url}>
                        {L(p, "text")}
                        <span aria-hidden="true"> ↗</span>
                      </a>
                    </li>
                  ) : (
                    <li key={p.id} className={classes.featureLink}>
                      {L(p, "text")}
                    </li>
                  )
                )}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* ---------------- Jamoa ---------------- */}
      {team.length > 0 && (
        <section className={`${classes.section} ${classes.teamSection}`}>
          <div className={classes.splitHead}>
            <div>
              <span className={classes.eyebrow}>{T("teamEyebrow")}</span>
              {L(teamSection, "title") && (
                <h2 className={classes.displayTitle}>{L(teamSection, "title")}</h2>
              )}
            </div>
            {L(teamSection, "description") && (
              <p className={classes.lead}>{L(teamSection, "description")}</p>
            )}
          </div>

          <div className={classes.teamGrid}>
            {team.map((m) => (
              <div key={m.id} className={classes.teamCard}>
                {L(m, "department") && (
                  <span className={classes.teamDept}>{L(m, "department")}</span>
                )}
                {m.photo && (
                  <img src={mediaUrl(m.photo)} alt="" className={classes.teamPhoto} />
                )}
                <h3 className={classes.teamName}>{m.name}</h3>
                {L(m, "position") && (
                  <p className={classes.teamPosition}>{L(m, "position")}</p>
                )}
                {L(m, "description") && (
                  <p className={classes.teamText}>{L(m, "description")}</p>
                )}
                {m.email && (
                  <a className={classes.teamEmail} href={`mailto:${m.email}`}>
                    {m.email}
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- Strategiya ---------------- */}
      {strategy.length > 0 && (
        <section className={`${classes.section} ${classes.strategy}`}>
          <span className={classes.eyebrow}>{T("strategyEyebrow")}</span>
          <h2 className={classes.displayTitle}>{T("strategyTitle")}</h2>

          <div className={classes.strategyGrid}>
            {strategy.map((pillar, idx) => (
              <div key={pillar.id} className={classes.strategyItem}>
                <span className={classes.strategyNo}>
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className={classes.strategyTitle}>{L(pillar, "title")}</h3>
                {L(pillar, "description") && (
                  <p className={classes.strategyText}>{L(pillar, "description")}</p>
                )}
                {pillar.link_url && (
                  <a className={classes.textLink} href={pillar.link_url}>
                    {T("more")}
                    <span aria-hidden="true"> ↗</span>
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- Manzil ---------------- */}
      {contact && (L(contact, "title") || L(contact, "address")) && (
        <section className={classes.contact}>
          <div className={classes.contactInner}>
            <div>
              <span className={classes.eyebrowLight}>{T("contactEyebrow")}</span>
              {L(contact, "title") && (
                <h2 className={classes.contactTitle}>{L(contact, "title")}</h2>
              )}
              {L(contact, "note") && (
                <p className={classes.contactNote}>{L(contact, "note")}</p>
              )}
            </div>

            <div className={classes.contactRight}>
              {L(contact, "address") && (
                <address className={classes.contactAddress}>
                  {L(contact, "address")}
                </address>
              )}
              {contact.map_url && (
                <a
                  className={classes.contactLink}
                  href={contact.map_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {T("map")}
                  <span aria-hidden="true"> ↗</span>
                </a>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SDGGoals;
